require('../bootConfig');
import '@babel/polyfill';
import Vue from 'vue';
import VueRouter from 'vue-router';
import Vuex, { mapState } from 'vuex';

import App from './App.vue';
import router from './router';
import iapStore from './store';

import backButton from '@admin/backButton.vue';
import loader from '@shared/loader.vue';
import tinymce from '@shared/TinymceNew.vue';
import modal from '@shared/Modal.vue';
import Notification from '@shared/Notification.vue';
import BaseInput from '@shared/BaseInput.vue';
import BaseSelect from '@shared/BaseSelect.vue';
import BaseRadioGroup from '@shared/BaseRadioGroup.vue';
import BaseDatePicker from '@shared/BaseDatePicker.vue';

import BaseAccordionList from '@shared/BaseAccordionList.vue';
import BaseAccordionPanelWithRadio from '@shared/BaseAccordionPanelWithRadio.vue';
import BaseAccordionPanel from '@shared/BaseAccordionPanel.vue';
import BaseSelectionPanel from '@shared/BaseSelectionPanel.vue';
import SaveText from '@authoringPlatform/shared/SaveText.vue';
import datePicker from 'vue-bootstrap-datetimepicker';
import DeleteModal from '@authoringPlatform/shared/DeleteModal.vue';
import popover from '@shared/Popover.vue';
import { BTable } from 'bootstrap-vue';

import 'bootstrap-sass/assets/javascripts/bootstrap/collapse.js';
import 'bootstrap-sass/assets/javascripts/bootstrap/dropdown.js';
import 'bootstrap-sass/assets/javascripts/bootstrap/modal.js';
import 'bootstrap-sass/assets/javascripts/bootstrap/tooltip.js';
import 'bootstrap-sass/assets/javascripts/bootstrap/popover.js';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { required, minLength, isDateAfter, minValue, dateExpiration } from '@utils/validation';

import * as Sentry from '@sentry/vue';
import { Integrations } from '@sentry/tracing';
import * as os from 'os';

//if (!os.hostname().includes('local')) {
Sentry.init({
  Vue,
  dsn: process.env.SENTRY_DSN_UI,
  integrations: [new Integrations.BrowserTracing()],
  environment: process.env.SERVER,
  // Track child components and see more details about the rendering process
  tracingOptions: {
    trackComponents: true,
  },

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.5,
  // Call original Vue’s logError function as well
  logErrors: process.env.SERVER === 'awsprod' ? false : true,
  // stack traces are automatically attached to all messages logged
  attachStacktrace: true,
  // Sentry will send all Vue components’ props for logging purpose
  attachProps: true,
});
//}

Vue.use(VueRouter);
Vue.use(Vuex);

const store = new Vuex.Store(iapStore);

window.Highcharts = require('highcharts');
require('highcharts/highcharts-more')(Highcharts);
require('highcharts/modules/solid-gauge')(Highcharts);
require('highcharts/modules/heatmap')(Highcharts);

Vue.component('b-table', BTable);

Vue.component('circle-icon', {
  props: ['radius', 'color'],
  render(createElem) {
    return createElem(
      'svg',
      {
        attrs: {
          height: this.radius * 2,
          width: this.radius * 2,
        },
      },
      [
        createElem('circle', {
          attrs: {
            cx: this.radius,
            cy: this.radius,
            r: this.radius,
            fill: this.color,
          },
        }),
      ]
    );
  },
});
Vue.component('rect-icon', {
  props: ['size', 'color'],
  render(createElem) {
    return createElem(
      'svg',
      {
        attrs: {
          height: this.size,
          width: this.size,
        },
      },
      [
        createElem('rect', {
          attrs: {
            width: this.size,
            height: this.size,
            fill: this.color,
          },
        }),
      ]
    );
  },
});

// invoke vee-validate extend methods in the entry
required();
minLength();
isDateAfter();
minValue();
dateExpiration();

// invoke vee-validate's component in the entry
Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);

Vue.use(datePicker);
Vue.component('loader', loader);
Vue.component('SaveText', SaveText);
Vue.component('backButton', backButton);
Vue.component('wysiwyg', tinymce);
Vue.component('modal', modal);
Vue.component('notify', Notification);
Vue.component('popover', popover);
Vue.component('DeleteModal', DeleteModal);
Vue.component('accordion-list', BaseAccordionList);
Vue.component('accordion-panel', BaseAccordionPanel);
Vue.component('accordion-panel-radio', BaseAccordionPanelWithRadio);
Vue.component('BaseInput', BaseInput);
Vue.component('BaseSelect', BaseSelect);
Vue.component('BaseRadioGroup', BaseRadioGroup);
Vue.component('BaseDatePicker', BaseDatePicker);
Vue.component('BaseSelectionPanel', BaseSelectionPanel);

Vue.filter('formatTime', (time) => {
  const totalSecs = time / 1000;
  const rawMins = Math.floor(totalSecs / 60);
  const formattedMins = rawMins >= 10 ? rawMins : `0${rawMins}`;
  const rawSecs = totalSecs % 60;
  const formattedSecs = rawSecs >= 10 ? rawSecs : `0${rawSecs}`;

  return `${formattedMins}m:${formattedSecs}s`;
});

Vue.filter('formatScore', (score) => {
  if (score > 0) {
    return `+${score}`;
  }
  if (score < 0) {
    return String(score);
  }
});

Vue.mixin({
  computed: {
    ...mapState('editMode', ['tooltips']),
  },
});
Vue.directive('click-outside', {
  bind: function (el, binding, vNode) {
    // Provided expression must evaluate to a function.
    if (typeof binding.value !== 'function') {
      const compName = vNode.context.name;
      let warn = `[Vue-click-outside:] provided expression '${binding.expression}' is not a function, but has to be`;
      if (compName) {
        warn += `Found in component '${compName}'`;
      }

      console.warn(warn);
    }

    // Define Handler and cache it on the element
    const ignoreFocusOutID = vNode.context.ignoreOnFocusOut;
    const bubble = binding.modifiers.bubble;
    const handler = (e) => {
      let $ignoreEl = document.getElementById(ignoreFocusOutID);
      if (ignoreFocusOutID && $ignoreEl && $ignoreEl.contains(e.target)) return;
      if (bubble || (!el.contains(e.target) && el !== e.target)) {
        binding.value(e);
      }
    };

    el.__vueClickOutside__ = handler;

    document.addEventListener('click', handler);
  },

  unbind: function (el, binding) {
    document.removeEventListener('click', el.__vueClickOutside__);
    el.__vueClickOutside__ = null;
  },
});

Object.defineProperty(Vue.prototype, '$EventBus', { value: new Vue() });

new Vue({
  el: '#app',
  router,
  store,
  render: (h) => h(App),
});

export { store };
